import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Printer } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "@/lib/brand";

export default function BatchLabels() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const [shipments, setShipments] = useState([]);

  useEffect(() => {
    Promise.all(ids.map((id) => api.get(`/shipments/${id}`).then((r) => r.data).catch(() => null)))
      .then((list) => setShipments(list.filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-8 print:bg-white print:p-0"
         data-testid="batch-labels-page">
      <div className="mx-auto max-w-5xl print:max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
          <Button variant="ghost" className="gap-2 pl-0 text-sm text-slate-600"
                  onClick={() => navigate("/admin")} data-testid="batch-back-button">
            <ArrowLeft className="h-4 w-4" /> Retour au tableau de bord
          </Button>
          <Button className="gap-2 bg-[#1E3A8A] font-semibold text-white hover:bg-[#172d6d]"
                  onClick={() => window.print()} data-testid="batch-print-button">
            <Printer className="h-4 w-4" /> Imprimer {shipments.length} étiquette(s)
          </Button>
        </div>

        <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900 print:hidden">
          Étiquettes à imprimer
        </h1>

        <p className="mt-1 text-sm text-slate-600 print:hidden">
          Format autocollant A4 : 2 colonnes × 4 lignes (8 étiquettes de 105 × 74 mm par page).
        </p>

        <div className="labels-sheet mt-6 grid gap-4 sm:grid-cols-2" data-testid="batch-labels-grid">
          {shipments.map((s) => (
            <div key={s.id}
                 className="break-inside-avoid rounded-xl border-2 border-dashed border-slate-300 bg-white p-4"
                 data-testid={`batch-label-${s.tracking_number}`}>
              <div className="flex items-center justify-between gap-2">
                <img src={LOGO_URL} alt="Haba Trans" className="h-7 w-auto object-contain" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  {s.shipping_type === "aerien" ? "Aérien" : "Maritime"} · {s.weight_kg} kg
                </span>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <QRCodeSVG value={`${window.location.origin}/suivi/${s.tracking_number}`} size={96}
                           level="M" fgColor="#1E3A8A" />
                <div className="min-w-0">
                  <p className="font-mono text-base font-extrabold tracking-wider text-[#1E3A8A]">
                    {s.tracking_number}
                  </p>
                  <p className="mt-1 truncate text-sm font-semibold text-slate-800">
                    {s.recipient.name}
                  </p>
                  <p className="truncate text-xs text-slate-600">
                    {s.destination_city} · {s.recipient.phone}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Livraison est. : {formatDate(s.estimated_delivery)}
                  </p>
                </div>
              </div>
            </div>
          ))}
          {shipments.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="batch-labels-empty">
              Aucune livraison sélectionnée.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
